/**
 * 教师学科分配(Jarod 2026-08-14)。学科成员关系决定:考试/作业/教学
 * 计划的学科标签下拉、学科助教绑定、学科知识库可见性。保存后触发
 * employees/sync 让学科助教即刻生效。
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { authHeaders, useSubjects } from "@/api/school";

function useTeacherSubjects(novaUserId: string) {
  return useQuery({
    queryKey: ["admin", "teacher-subjects", novaUserId],
    queryFn: async () => {
      const r = await fetch(`/api/admin/teachers/${novaUserId}/subjects`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return (await r.json()) as { id: string; slug: string; name: string }[];
    },
  });
}

export function TeacherSubjectsCell({ novaUserId, onEdit }: { novaUserId: string; onEdit: () => void }) {
  const q = useTeacherSubjects(novaUserId);
  return (
    <button type="button" onClick={onEdit} className="group flex flex-wrap items-center gap-1 text-left">
      {(q.data ?? []).map((s) => (
        <span key={s.id} className="rounded bg-sky-50 border border-sky-200 px-1.5 text-[11px] text-sky-700">
          {s.name}
        </span>
      ))}
      <span className="text-[11px] text-slate-400 opacity-0 group-hover:opacity-100">✎</span>
      {(q.data ?? []).length === 0 && <span className="text-xs text-slate-400">—</span>}
    </button>
  );
}

export function TeacherSubjectsDialog({
  novaUserId,
  teacherName,
  onClose,
}: {
  novaUserId: string;
  teacherName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const all = useSubjects();
  const mine = useTeacherSubjects(novaUserId);
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const effective = picked ?? new Set((mine.data ?? []).map((s) => s.id));

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch(`/api/admin/teachers/${novaUserId}/subjects`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ subject_ids: [...effective] }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // 学科助教同步(幂等;失败不阻塞——文件已写,重启也会生效)
      await fetch("/api/school/employees/sync", { method: "POST", headers: authHeaders() }).catch(() => {});
      void qc.invalidateQueries({ queryKey: ["admin", "teacher-subjects", novaUserId] });
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const subjects = (all.data ?? []).filter((s) => s.status !== "archived");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-slate-800 mb-1">
          {t("schoolDetail.assignSubjects", { name: teacherName })}
        </h3>
        <p className="mb-3 text-xs text-slate-500">{t("schoolDetail.assignSubjectsHint")}</p>
        <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto">
          {subjects.map((s) => (
            <label key={s.id} className="flex items-center gap-2 rounded border border-slate-200 px-2 py-1.5 hover:bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                checked={effective.has(s.id)}
                onChange={() => {
                  const next = new Set(effective);
                  if (next.has(s.id)) next.delete(s.id);
                  else next.add(s.id);
                  setPicked(next);
                }}
              />
              <span className="text-sm text-slate-800">{s.name}</span>
            </label>
          ))}
        </div>
        {err && <p className="mt-2 text-xs text-rose-500">{err}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="px-3 py-1.5 text-sm rounded border border-slate-200 text-slate-600">
            {t("common.cancel")}
          </button>
          <button type="button" onClick={() => void save()} disabled={busy} className="px-4 py-1.5 text-sm rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50">
            {busy ? t("common.loading") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
